import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';

import {
  AccountBalanceWallet,
  Casino,
  TrendingUp,
  History,
  Assessment,
  Star,
  People,
  AccountTree,
  Percent
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PayoutMatrix from '../components/PayoutMatrix';
import CommissionDisplay from '../components/CommissionDisplay';

interface DashboardStats {
  totalBets: number;
  totalWinnings: number;
  totalLosses: number;
  recentBets: Array<{
    id: number;
    date: string;
    counter: string;
    number: string;
    amount: number;
    status: 'pending' | 'won' | 'lost';
  }>;
  recentResults: Array<{
    id: number;
    counter: string;
    date: string;
    firstPrize: string;
    secondPrize: string;
    thirdPrize: string;
  }>;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await axios.get('/dashboard/stats');
      return response.data;
    },
    // Mock data for development
    initialData: {
      totalBets: 25,
      totalWinnings: 1250.00,
      totalLosses: 750.00,
      recentBets: [
        {
          id: 1,
          date: '2024-01-15',
          counter: 'Magnum',
          number: '1234',
          amount: 50,
          status: 'pending'
        },
        {
          id: 2,
          date: '2024-01-14',
          counter: 'Sports Toto',
          number: '5678',
          amount: 25,
          status: 'won'
        }
      ],
      recentResults: [
        {
          id: 1,
          counter: 'Magnum',
          date: '2024-01-15',
          firstPrize: '1234',
          secondPrize: '5678',
          thirdPrize: '9012'
        }
      ]
    }
  });

  const quickActions = [
    // {
    //   title: 'Place Bet',
    //   description: 'Place a new lottery bet',
    //   icon: <Casino />,
    //   color: 'primary',
    //   action: () => navigate('/bet')
    // },
    {
      title: 'View Results',
      description: 'Check latest lottery results',
      icon: <TrendingUp />,
      color: 'secondary',
      action: () => navigate('/results')
    },
    // {
    //   title: 'Transaction History',
    //   description: 'View your betting history',
    //   icon: <History />,
    //   color: 'info',
    //   action: () => navigate('/history')
    // },
    {
      title: 'Settlement Report',
      description: 'View your daily settlement reports',
      icon: <Assessment />,
      color: 'info',
      action: () => navigate('/settlement-report')
    },
    {
      title: 'Downline Management',
      description: 'Manage your agent network',
      icon: <AccountTree />,
      color: 'success',
      action: () => navigate('/downline-management')
    },
    {
      title: 'Sales Management',
      description: 'Manage your sales percentages',
      icon: <Percent />,
      color: 'warning',
      action: () => navigate('/sales-management')
    },
    // {
    //   title: 'Withdrawal',
    //   description: 'Request a withdrawal',
    //   icon: <AccountBalanceWallet />,
    //   color: 'success',
    //   action: () => navigate('/withdrawal')
    // }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'won': return 'success';
      case 'lost': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Welcome back, {user?.username}
      </Typography>

      {/* Stats Overview */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <AccountBalanceWallet color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h6">Credits</Typography>
            <Typography variant="h4" color="primary">
              ${user?.credits?.toFixed(2) || '0.00'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Casino color="secondary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h6">Total Bets</Typography>
            <Typography variant="h4" color="secondary">
              {stats?.totalBets || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Star color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h6">Total Winnings</Typography>
            <Typography variant="h4" color="success.main">
              ${stats?.totalWinnings?.toFixed(2) || '0.00'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Assessment color="error" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h6">Total Losses</Typography>
            <Typography variant="h4" color="error.main">
              ${stats?.totalLosses?.toFixed(2) || '0.00'}
            </Typography>
          </Paper>
        </Grid>
      </Grid> */}

      {/* Quick Actions */}
      <Typography variant="h5" gutterBottom>
        Quick Actions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickActions.map((action, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {React.cloneElement(action.icon, { color: action.color as any })}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {action.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" color={action.color as any} onClick={action.action}>
                  Go
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Bets */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Bets
            </Typography>
            <List>
              {stats?.recentBets?.map((bet) => (
                <ListItem key={bet.id}>
                  <ListItemIcon>
                    <Casino />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${bet.counter} - ${bet.number}`}
                    secondary={`${bet.date} - $${bet.amount}`}
                  />
                  <Chip
                    label={bet.status}
                    color={getStatusColor(bet.status) as any}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
            <Button fullWidth onClick={() => navigate('/history')}>
              View All History
            </Button>
          </Paper>
        </Grid>

        {/* Recent Results */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Latest Results
            </Typography>
            <List>
              {stats?.recentResults?.map((result) => (
                <ListItem key={result.id}>
                  <ListItemIcon>
                    <TrendingUp />
                  </ListItemIcon>
                  <ListItemText
                    primary={result.counter}
                    secondary={`${result.date} - 1st: ${result.firstPrize}`}
                  />
                </ListItem>
              ))}
            </List>
            <Button fullWidth onClick={() => navigate('/results')}>
              View All Results
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Commission Display */}
      <Box sx={{ mt: 4 }}>
        <CommissionDisplay />
      </Box>

      {/* Payout Matrix */}
      <Box sx={{ mt: 4 }}>
        <PayoutMatrix />
      </Box>
    </Container>
  );
};

export default Dashboard;